import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { State, Action, StateContext, Selector, StateToken } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { tap } from 'rxjs/operators';
import { IComment } from '../model/comment.model';
import { CommentsApiService } from '../services/comments-api.service';
import {
  LoadComments,
  AddComment,
  UpdateComment,
  DeleteComment,
  ClearComments,
} from './comments.actions';

export interface CommentsStateModel {
  comments: IComment[];
  commentsForPopup: IComment[];
}

const COMMENTS_STATE_TOKEN = new StateToken<CommentsStateModel>('comments');

@State<CommentsStateModel>({
  name: COMMENTS_STATE_TOKEN,
  defaults: {
    comments: [],
    commentsForPopup: [],
  },
})
@Injectable()
export class CommentsState {
  constructor(private api: CommentsApiService) {}

  @Action(LoadComments)
  loadComments(
    ctx: StateContext<CommentsStateModel>,
    { entityType, entityId, isInPopup }: LoadComments
  ) {
    return this.api.getComments(entityType, entityId).pipe(
      tap((comments) => {
        ctx.patchState(isInPopup ? { commentsForPopup: comments } : { comments });
      })
    );
  }

  @Action(AddComment)
  @OnSuccess({ message: 'commentAdded' })
  addComment(
    ctx: StateContext<CommentsStateModel>,
    { entityType, comment, isInPopup }: AddComment
  ) {
    return this.api.addComment(entityType, comment).pipe(
      tap(() => {
        ctx.dispatch(new LoadComments(entityType, comment.entityId, !!isInPopup));
      })
    );
  }

  @Action(UpdateComment)
  @OnSuccess({ message: 'commentUpdated' })
  updateComment(
    ctx: StateContext<CommentsStateModel>,
    { entityType, id, comment, isInPopup }: UpdateComment
  ) {
    return this.api.updateComment(entityType, id, comment).pipe(
      tap(() => {
        const state = ctx.getState();
        const currentComments = isInPopup ? state.commentsForPopup : state.comments;
        const commentIndex = currentComments.findIndex((c) => c.id === id);

        if (commentIndex !== -1) {
          const updatedComments = [...currentComments];
          updatedComments[commentIndex] = {
            ...updatedComments[commentIndex],
            content: comment.content,
            attachments: comment.attachments,
            mentionedUsers: comment.mentionedUsers,
            lastUpdate: new Date().toISOString(),
            lastUpdatedBy: updatedComments[commentIndex].createdBy,
          };
          if (isInPopup) {
            ctx.patchState({ commentsForPopup: updatedComments });
          } else {
            ctx.patchState({ comments: updatedComments });
          }
        }
      })
    );
  }

  @Action(DeleteComment)
  @OnSuccess({ message: 'commentDeleted' })
  deleteComment(
    ctx: StateContext<CommentsStateModel>,
    { entityType, entityId, id, isInPopup }: DeleteComment
  ) {
    return this.api.deleteComment(entityType, entityId, id).pipe(
      tap(() => {
        const state = ctx.getState();
        const comments = (isInPopup ? state.commentsForPopup : state.comments).filter(
          (c) => c.id !== id
        );
        ctx.patchState(isInPopup ? { commentsForPopup: comments } : { comments });
      })
    );
  }

  @Action(ClearComments)
  clearComments(ctx: StateContext<CommentsStateModel>) {
    ctx.patchState({ comments: [] });
  }

  @Selector()
  static getComments(state: CommentsStateModel) {
    return state.comments;
  }

  @Selector()
  static getCommentsForPopup(state: CommentsStateModel) {
    return state.commentsForPopup;
  }
}
